import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export interface CameraState {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov: number;
}

/**
 * 相机状态定义
 */
export const CAMERA_STATES = {
  // 初始俯视状态 - 从上方远处俯视
  topView: {
    position: new THREE.Vector3(0, 25, 15),
    target: new THREE.Vector3(0, -1, 0),
    fov: 45
  },
  // 中间状态 - 略近一些
  midView: {
    position: new THREE.Vector3(5, 15, 10),
    target: new THREE.Vector3(0, 0, 0),
    fov: 50
  },
  // 3D 视角 - 45度斜视
  threeDView: {
    position: new THREE.Vector3(12, 10, 12),
    target: new THREE.Vector3(0, 0.5, 0),
    fov: 55
  },
  // Wall 阶段：更电影化的 framing（更稳、更“建筑可视化”）
  wallFocusView: {
    position: new THREE.Vector3(10, 11, 14),
    target: new THREE.Vector3(0, 0.35, 0),
    fov: 52
  },
  // Furniture 阶段：横向正对家具展示（镜头正对模型底边）
  furnitureDollyView: {
    position: new THREE.Vector3(0, 6, 10),
    target: new THREE.Vector3(0.3, 0.35, 0),
    fov: 52
  },
  // SDK 阶段：鸟瞰全局（显示整个模型，地图不放大）
  sdkFollowPathTop: {
    position: new THREE.Vector3(10, 12.5, 14),
    target: new THREE.Vector3(0, 0.35, 0),
    fov: 50
  }
};

/**
 * 创建相机
 */
export function createCamera(aspect: number): THREE.PerspectiveCamera {
  const camera = new THREE.PerspectiveCamera(
    45,           // FOV
    aspect,       // aspect ratio
    0.1,          // near
    1000          // far
  );

  // 初始位置 - 俯视
  camera.position.copy(CAMERA_STATES.topView.position);
  camera.lookAt(CAMERA_STATES.topView.target);

  return camera;
}

/**
 * 创建轨道控制器 (可选，用于调试)
 */
export function createOrbitControls(
  camera: THREE.PerspectiveCamera,
  domElement: HTMLElement
): OrbitControls {
  const controls = new OrbitControls(camera, domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.enablePan = false;
  controls.minDistance = 5;
  controls.maxDistance = 50;
  controls.maxPolarAngle = Math.PI / 2.2;
  controls.enabled = false; // 默认禁用，动画控制相机

  return controls;
}

/**
 * 相机状态插值
 * 使用线性插值平滑过渡（优化版：避免每帧创建新对象）
 */
const _tempTarget = new THREE.Vector3();

export function lerpCameraState(
  camera: THREE.PerspectiveCamera,
  from: CameraState,
  to: CameraState,
  progress: number
): void {
  // 球面线性插值位置
  camera.position.lerpVectors(from.position, to.position, progress);

  // 线性插值目标点（复用临时对象）
  _tempTarget.lerpVectors(from.target, to.target, progress);
  camera.lookAt(_tempTarget);

  // 线性插值 FOV
  camera.fov = THREE.MathUtils.lerp(from.fov, to.fov, progress);
  camera.updateProjectionMatrix();
}

/**
 * 设置相机到指定状态
 */
export function setCameraState(
  camera: THREE.PerspectiveCamera,
  state: CameraState
): void {
  camera.position.copy(state.position);
  camera.lookAt(state.target);
  camera.fov = state.fov;
  camera.updateProjectionMatrix();
}

/**
 * 相机围绕中心点旋转
 * 用于 3D 视角效果
 */
export function rotateCameraAroundY(
  camera: THREE.PerspectiveCamera,
  center: THREE.Vector3,
  angle: number
): void {
  // 计算相机相对中心的位置
  const relative = new THREE.Vector3().subVectors(camera.position, center);

  // 绕 Y 轴旋转
  relative.applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);

  // 应用旋转
  camera.position.copy(center).add(relative);
  camera.lookAt(center);
}

export type { OrbitControls };
